import { Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import {
  Archive,
  ArchiveRestore,
  CalendarDays,
  CloudUpload,
  Loader2,
  Menu,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useBulkDeleteEvents } from '~entities/events/api/bulk-delete-events';
import { useGetAllEventsForCSV } from '~entities/events/api/get-all-events-for-csv';
import {
  type EventDetails,
  useGetAllEventsWithSearch,
  useUpdateEventArchiveStatus,
} from '~entities/events/index';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeaderWithFilter } from '~shared/components/table';
import { InstructorListCell } from '~shared/components/table/instructor-list-cell';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { DAYS, type Days } from '~shared/constants/days';
import { exportToCSV } from '~shared/helpers/csv';
import {
  extractDateOnly,
  formatAgeBound,
  formatAgeRange,
} from '~shared/helpers/datetime';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import { useFilterPersistence } from '~shared/hooks/useFilterPersistence';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~shared/shadcn/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { DeleteEventDialog } from './delete-event-dialog';
import {
  countActiveEventListFilters,
  EVENT_LIST_FILTER_ALL,
} from './event-list-table.helpers';

// Create a column helper
const columnHelper = createColumnHelper<EventDetails>();

const ArchiveEventMenuItem = ({ eventItem }: { eventItem: EventDetails }) => {
  const archiveMutation = useUpdateEventArchiveStatus();

  const handleArchive = async () => {
    try {
      await archiveMutation.mutateAsync({
        id: eventItem._id,
        isArchive: !eventItem.isArchive,
      });
      toast.success(
        `Event ${eventItem.isArchive ? 'unarchived' : 'archived'} successfully`,
      );
    } catch (error) {
      console.error('Archive event error:', error);
      toast.error(
        `Failed to ${eventItem.isArchive ? 'unarchive' : 'archive'} event`,
      );
    }
  };

  return (
    <DropdownMenuItem
      onSelect={(event) => {
        event.preventDefault();
        void handleArchive();
      }}
      disabled={archiveMutation.isPending}
    >
      {archiveMutation.isPending ? (
        <>
          <Loader2 className='mr-2 size-4 animate-spin' />
          {eventItem.isArchive ? 'Unarchiving...' : 'Archiving...'}
        </>
      ) : eventItem.isArchive ? (
        <>
          <ArchiveRestore className='mr-2 size-4' />
          Unarchive
        </>
      ) : (
        <>
          <Archive className='mr-2 size-4' />
          Archive
        </>
      )}
    </DropdownMenuItem>
  );
};

export const EventListTable = () => {
  const navigate = useNavigate({ from: '/events' });
  const { hasAdminAccess, hasSuperAdminAccess } = useCurrentUserRole();

  // Define columns
  const cols = [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className='px-4'>
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => {
              table.toggleAllPageRowsSelected(!!value);
            }}
            aria-label='Select all'
          />
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className='px-4'>
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => {
                row.toggleSelected(!!value);
              }}
              aria-label='Select row'
            />
          </div>
        );
      },
    }),
    columnHelper.accessor('title', {
      id: 'Event Name',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Event Name'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className='flex flex-wrap items-center gap-2'>
            {info.row.original.isArchive === true ? (
              <Badge
                variant='secondary'
                className='shrink-0 text-xs font-normal'
              >
                Archived
              </Badge>
            ) : null}
            <Button
              variant={'link'}
              className='capitalize text-custom-secondary-1'
              asChild
            >
              <Link
                to={`/events/$eventId/enrolled-students`}
                params={{
                  eventId: info.row.original._id.toString(),
                }}
              >
                {value}
              </Link>
            </Button>
          </div>
        );
      },
    }),
    columnHelper.accessor('session', {
      header: ({ column }) => {
        return (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={column}
            title='Sessions'
          />
        );
      },
      cell: (info) => {
        const session = info.getValue();
        return <div className='px-4 capitalize'>{session.name || ''}</div>;
      },
    }),
    columnHelper.accessor('location', {
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Location'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        return <div className='px-4 capitalize'>{value.name || ''}</div>;
      },
    }),
    columnHelper.accessor('instructor', {
      id: 'Instructor',
      header: (info) => (
        <DataTableColumnHeader
          className='min-w-[200px] text-center text-black'
          column={info.column}
          title='Instructor'
        />
      ),
      cell: (info) => {
        const instructors = info.getValue();
        return <InstructorListCell instructors={instructors} />;
      },
    }),
    columnHelper.accessor(
      (v) => {
        return formatAgeRange(v.startYear, v.startMonth, v.endYear, v.endMonth);
      },
      {
        id: 'Age (Range)',
        header: (info) => (
          <DataTableColumnHeaderWithFilter
            className='pl-4 text-black'
            info={info}
            title='Age (Range)'
          />
        ),
        cell: (info) => {
          return <div className='px-4'>{info.getValue() || ''}</div>;
        },
      },
    ),
    columnHelper.accessor('room', {
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Room'
        />
      ),
      cell: (info) => {
        const room = info.getValue();
        return <div className='px-4 capitalize'>{room?.name ?? 'N/A'}</div>;
      },
    }),
    columnHelper.accessor('oneTime', {
      id: 'Frequency',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Frequency'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        return <div className='px-4'>{value ? 'One Time' : 'Recurring'}</div>;
      },
    }),
    columnHelper.accessor('remainingSeats', {
      id: 'Remaining Seats',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Remaining Seats'
        />
      ),
      cell: (info) => <div className='px-4'>{info.getValue()}</div>,
    }),
    columnHelper.accessor('days', {
      id: 'Event Days',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Event Days'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className='flex gap-2 px-4'>
            {Array.isArray(value) ? (
              value.map((v) => <Badge key={v}>{v}</Badge>)
            ) : (
              <span>{value}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor(
      (v) => {
        if (!v.availability?.length) {
          return 'No availability';
        }

        const firstAvailability = v.availability[0];
        const isValidDate = (date: string) => !isNaN(Date.parse(date));

        if (
          !firstAvailability ||
          !isValidDate(firstAvailability.startTime) ||
          !isValidDate(firstAvailability.endTime)
        ) {
          return 'Invalid date format';
        }

        const formattedStartTime = format(
          parse(
            firstAvailability.startTime,
            'MMM d, yyyy, h:mm:ss a',
            new Date(),
          ),
          'p',
        );
        const formattedEndTime = format(
          parse(
            firstAvailability.endTime,
            'MMM d, yyyy, h:mm:ss a',
            new Date(),
          ),
          'p',
        );

        return `${formattedStartTime} - ${formattedEndTime}`;
      },
      {
        id: 'Time',
        header: (info) => (
          <DataTableColumnHeaderWithFilter
            className='pl-4 text-black'
            info={info}
            title='Time'
          />
        ),
        cell: (info) => (
          <div className='w-40 px-4'>{info.getValue() || ''}</div>
        ),
      },
    ),
    columnHelper.accessor('startDate', {
      id: 'Start Date',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Start Date'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        const extractedDate = value ? extractDateOnly(value) : null;
        if (!extractedDate) {
          return (
            <div className='flex gap-2 px-4'>
              <span>-</span>
            </div>
          );
        }

        // Use the date string directly with the format function
        // This avoids any Date object creation which can introduce timezone issues
        return (
          <div className='flex gap-2 px-4'>
            <span>
              {extractedDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor('endDate', {
      id: 'End Date',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='End Date'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        const extractedDate = value ? extractDateOnly(value) : null;
        if (!extractedDate) {
          return (
            <div className='flex gap-2 px-4'>
              <span>-</span>
            </div>
          );
        }

        // Use the date string directly with the format function
        // This avoids any Date object creation which can introduce timezone issues
        return (
          <div className='flex gap-2 px-4'>
            <span>
              {extractedDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor('tuitionFee', {
      id: 'Event Price',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Event Price'
        />
      ),
      cell: (info) => (
        <div className='px-4'>
          {currencyDetails?.symbol}
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('maxSize', {
      id: 'Max Size',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Max Size'
        />
      ),
      cell: (info) => <div className='px-4'> {info.getValue()}</div>,
    }),
    columnHelper.accessor('tags', {
      id: 'Tags',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Tags'
        />
      ),
      cell: (info) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const tags = info.getValue() || [];
        return (
          <div className='flex gap-2 px-4'>
            {tags.map((v) => {
              return (
                <Badge key={v._id} className='capitalize'>
                  {v.name || ''}
                </Badge>
              );
            })}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'action',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Action'
        />
      ),
      enableHiding: false,
      cell: (info) => {
        const eventItem = info.row.original;
        return (
          <div className='px-4'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={!hasSuperAdminAccess}>
                <Button variant='ghost' className='size-8 p-0'>
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem asChild>
                  <Link
                    to={`/events/$eventId/update`}
                    params={{
                      eventId: eventItem._id.toString(),
                    }}
                    className='flex items-center'
                  >
                    <Pencil className='mr-2 size-4' />
                    Edit
                  </Link>
                </DropdownMenuItem>
                <ArchiveEventMenuItem eventItem={eventItem} />
                <DropdownMenuItem
                  className='text-red-600'
                  onClick={() => {
                    setSelectedEvent(eventItem);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash className='mr-2 size-4' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  const { data: currencyDetails } = useGetCurrency();

  // Use the filter persistence hook
  const { filters, pagination, updateFilter, updatePagination } =
    useFilterPersistence<{
      query: string;
      day: string;
      tag: string;
      room: string;
      instructor: string;
      session: string;
      showArchivedEvents: boolean;
    }>({
      query: '',
      day: EVENT_LIST_FILTER_ALL,
      tag: EVENT_LIST_FILTER_ALL,
      room: EVENT_LIST_FILTER_ALL,
      instructor: EVENT_LIST_FILTER_ALL,
      session: EVENT_LIST_FILTER_ALL,
      showArchivedEvents: false,
    });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: eventsData, isLoading } = useGetAllEventsWithSearch({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    name: filters.query,
    instructor:
      filters.instructor !== EVENT_LIST_FILTER_ALL
        ? filters.instructor
        : undefined,
    tags: filters.tag !== EVENT_LIST_FILTER_ALL ? filters.tag : undefined,
    days:
      filters.day !== EVENT_LIST_FILTER_ALL ? (filters.day as Days) : undefined,
    room: filters.room !== EVENT_LIST_FILTER_ALL ? filters.room : undefined,
    session:
      filters.session !== EVENT_LIST_FILTER_ALL ? filters.session : undefined,
    isStudio: true,
    isArchive: filters.showArchivedEvents,
  });

  const data = eventsData?.data.events ?? [];
  const totalCount = eventsData?.pagination.totalCount ?? 0;

  const instructors = eventsData?.data.instructors ?? [];
  const tags = eventsData?.data.tags ?? [];
  const rooms = eventsData?.data.rooms ?? [];
  const sessions = eventsData?.data.sessions ?? [];

  const bulkDeleteMutation = useBulkDeleteEvents();

  const { refetch: getAllEventsForCSV } = useGetAllEventsForCSV({
    enabled: false,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventDetails | null>(null);
  const [eventActionsOpen, setEventActionsOpen] = useState(false);
  const activeEventFilterCount = countActiveEventListFilters(filters);

  const handleExport = async () => {
    const { data: AllEventsForCSVData } = await getAllEventsForCSV();

    const headers = [
      'Event Name',
      'Location',
      'Days',
      'Room',
      'Frequency',
      'Event Days',
      'Start Time',
      'End Time',
      'Duration',
      'Start Date',
      'End Date',
      'Max Size',
      'Registration Fee Amount',
      'Description',
      'Start Age',
      'End Age',
      'Instructor',
      'Tags',
      'Group',
      'Policy',
    ];

    const formattedData =
      AllEventsForCSVData?.map((event) => ({
        'Event Name': event.title ? capitalizeWords(event.title) : '',
        Location: event.location?.fieldName
          ? capitalizeWords(event.location.fieldName)
          : '',
        Room: event.room ? capitalizeWords(event.room) : '',
        Days: Array.isArray(event.days)
          ? event.days.map((day) => capitalizeWords(day)).join(', ')
          : event.days,
        Frequency: event.oneTime ? 'One Time' : 'Recurring',
        'Event Days': Array.isArray(event.days)
          ? event.days.map((day) => capitalizeWords(day)).join(', ')
          : event.days,
        'Start Time': new Date(event.startTime).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        }),
        'End Time': new Date(event.endTime).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        }),
        Duration: event.duration,
        'Start Date': event.startDate
          ? new Date(event.startDate).toLocaleDateString('en-US')
          : '-',
        'End Date': event.endDate
          ? new Date(event.endDate).toLocaleDateString('en-US')
          : '-',
        'Max Size': event.maxSize,
        'Registration Fee Amount': event.registrationFeeAmount,
        Description:
          event.description.length > 0
            ? event.description.charAt(0).toUpperCase() +
              event.description.slice(1)
            : '',
        'Start Age': formatAgeBound(event.startAge),
        'End Age': formatAgeBound(event.endAge),
        Instructor: event.instructor
          .map((instructor) => capitalizeWords(instructor))
          .join(', '),
        Tags: event.tags.map((tag) => capitalizeWords(tag)).join(', '),
        Group: event.group.map((group) => capitalizeWords(group)).join(', '),
        Policy: event.policyGroup
          .map((policy) => capitalizeWords(policy))
          .join(', '),
      })) ?? [];
    exportToCSV(
      formattedData,
      headers,
      `events-export-${new Date().toISOString()}.csv`,
    );
  };

  const renderEventFilters = (stacked: boolean) => {
    const triggerClassName = stacked
      ? 'w-full capitalize'
      : 'w-full capitalize md:w-[160px]';
    const archivedId = stacked
      ? 'showArchivedEvents-drawer'
      : 'showArchivedEvents';

    return (
      <>
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Room</label>
          ) : null}
          <Select
            value={filters.room}
            onValueChange={(value) => {
              updateFilter('room', value);
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder='Rooms' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EVENT_LIST_FILTER_ALL}>All Rooms</SelectItem>
              {rooms.map((room) => (
                <SelectItem
                  key={room._id}
                  value={room.name}
                  className='capitalize'
                >
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Tag</label>
          ) : null}
          <Select
            value={filters.tag}
            onValueChange={(value) => {
              updateFilter('tag', value);
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder='Tags' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EVENT_LIST_FILTER_ALL}>All Tags</SelectItem>
              {tags.map((tag) => (
                <SelectItem
                  key={tag._id}
                  value={tag.name}
                  className='capitalize'
                >
                  {tag.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Instructor</label>
          ) : null}
          <Select
            value={filters.instructor}
            onValueChange={(value) => {
              updateFilter('instructor', value);
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder='Instructor' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EVENT_LIST_FILTER_ALL}>
                All Instructors
              </SelectItem>
              {instructors.map((instructor) => (
                <SelectItem
                  key={instructor._id}
                  value={instructor.name}
                  className='capitalize'
                >
                  {instructor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Day</label>
          ) : null}
          <Select
            value={filters.day}
            onValueChange={(value) => {
              updateFilter('day', value);
            }}
          >
            <SelectTrigger
              className={stacked ? 'w-full' : 'w-full md:w-[160px]'}
            >
              <SelectValue placeholder='Class Day' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EVENT_LIST_FILTER_ALL}>All Days</SelectItem>
              {DAYS.map((day) => (
                <SelectItem key={day} value={day}>
                  {day}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Session</label>
          ) : null}
          <Select
            value={filters.session}
            onValueChange={(value) => {
              updateFilter('session', value);
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder='Session' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EVENT_LIST_FILTER_ALL}>
                All Sessions
              </SelectItem>
              {sessions.map((session) => (
                <SelectItem
                  key={session._id}
                  value={session.name}
                  className='capitalize'
                >
                  {session.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={stacked ? 'pt-1' : undefined}>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id={archivedId}
              checked={filters.showArchivedEvents}
              onCheckedChange={(checked) => {
                updateFilter('showArchivedEvents', checked === true);
              }}
            />
            <label
              htmlFor={archivedId}
              className='cursor-pointer text-sm font-medium'
            >
              Show Archived Events
            </label>
          </div>
        </div>
      </>
    );
  };

  const eventToolbarActions = (
    <>
      {hasSuperAdminAccess && Object.keys(rowSelection).length > 0 ? (
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              if (Object.keys(rowSelection).length > 1) {
                toast.error('Please select only one event to edit');
                return;
              }
              const selectedEventIndex = Object.keys(rowSelection)[0];
              if (!selectedEventIndex) return;

              const selectedEvent = data[parseInt(selectedEventIndex)];
              if (selectedEvent) {
                void navigate({
                  to: '/events/$eventId/update',
                  params: { eventId: selectedEvent._id.toString() },
                });
              }
            }}
          >
            <Pencil className='mr-2 size-4' />
            Edit Selected ({Object.keys(rowSelection).length})
          </Button>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => {
              const selectedEvents = Object.keys(rowSelection).map(
                (index) => data[parseInt(index)],
              );
              setSelectedEvent(selectedEvents[0] ?? null);
              setDeleteDialogOpen(true);
            }}
          >
            <Trash className='mr-2 size-4' />
            Delete Selected ({Object.keys(rowSelection).length})
          </Button>
        </div>
      ) : null}
      <div className='hidden flex-wrap gap-2 md:flex'>
        {hasAdminAccess && (
          <Button
            className='bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
            asChild
          >
            <Link to='/events/new'>
              <Plus />
              Add Events
            </Link>
          </Button>
        )}
        <Button asChild>
          <Link to='/event-calendar'>
            <CalendarDays />
            Event Calendar
          </Link>
        </Button>
        <Button variant={'outline'} onClick={handleExport}>
          <CloudUpload />
          Export
        </Button>
      </div>
    </>
  );

  const eventFilterControls = (
    <>
      <Sheet open={eventActionsOpen} onOpenChange={setEventActionsOpen}>
        <SheetTrigger asChild>
          <Button variant='outline' className='relative w-full md:hidden'>
            <Menu className='mr-2 size-4' />
            Event actions
            {activeEventFilterCount > 0 ? (
              <Badge className='ml-2' variant='secondary'>
                {activeEventFilterCount}
              </Badge>
            ) : null}
          </Button>
        </SheetTrigger>
        <SheetContent side='right' className='w-[300px] overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>Event actions</SheetTitle>
            <SheetDescription>
              Add or export events, open the calendar, or filter this list.
            </SheetDescription>
          </SheetHeader>
          <div className='mt-6 flex flex-col gap-3'>
            {hasAdminAccess && (
              <Button
                className='w-full justify-start bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
                asChild
              >
                <Link
                  to='/events/new'
                  onClick={() => {
                    setEventActionsOpen(false);
                  }}
                >
                  <Plus />
                  Add Events
                </Link>
              </Button>
            )}
            <Button className='w-full justify-start' asChild>
              <Link
                to='/event-calendar'
                onClick={() => {
                  setEventActionsOpen(false);
                }}
              >
                <CalendarDays />
                Event Calendar
              </Link>
            </Button>
            <Button
              variant='outline'
              className='w-full justify-start'
              onClick={() => {
                setEventActionsOpen(false);
                void handleExport();
              }}
            >
              <CloudUpload />
              Export
            </Button>
            {renderEventFilters(true)}
          </div>
        </SheetContent>
      </Sheet>
      <div className='hidden w-full flex-wrap items-center gap-2 md:flex md:w-auto'>
        {renderEventFilters(false)}
      </div>
    </>
  );

  return (
    <Card>
      <CardContent className='pt-6'>
        <div>
          <EnrollioTable
            ariaLabel='Events'
            totalLabel='Events'
            filterHeader={
              <>
                <EnrollioTableSearch
                  placeholder='Search events...'
                  value={filters.query}
                  onChange={(value) => {
                    updateFilter('query', value);
                  }}
                />
                {eventFilterControls}
              </>
            }
            toolbar={(table) => (
              <>
                {eventToolbarActions}
                <EnrollioTableColumnToggle table={table} />
              </>
            )}
            columns={cols}
            data={data}
            rowCount={totalCount}
            isUpdating={isLoading}
            sorting={sorting}
            onSortingChange={setSorting}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={pagination}
            onPaginationChange={updatePagination}
            manualPagination
            enableRowSelection={true}
            showSelectionCount
          />
        </div>
      </CardContent>
      <DeleteEventDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedEvent={selectedEvent}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedEvent(null);
          setRowSelection({});
        }}
        isBulkDelete={Object.keys(rowSelection).length > 1}
        onDelete={() => {
          const selectedEvents = Object.keys(rowSelection).map(
            (index) => data[parseInt(index)],
          );
          bulkDeleteMutation.mutate({
            eventIds: selectedEvents.flatMap(
              (selectedEvent) => selectedEvent?._id ?? [],
            ),
          });
        }}
      />
    </Card>
  );
};
